import { In } from 'typeorm'
import { Arg, Args, Ctx, Directive, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import {
  Domain,
  getQueryBuilderFromListParams,
  getRepository,
  getISOStringsForPeriod,
  ListParam
} from '@things-factory/shell'
import { ActivityInstance } from '@things-factory/worklist'

import { DataKeySet } from '../data-key-set/data-key-set.js'
import { DataSetHistory } from '../data-set-history/data-set-history.js'
import { DataItem } from '../data-set/data-item-type.js'
import { DataSet } from '../data-set/data-set.js'
import { DataOoc } from '../data-ooc/data-ooc.js'
import { DataSample } from './data-sample.js'
import { DataSampleList } from './data-sample-type.js'

@Resolver(DataSample)
export class DataSampleQuery {
  @Directive('@privilege(category: "data-sample", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSample, {
    description:
      'Retrieves a single data sample by its unique identifier. This query fetches detailed information about a specific data sample including its values, metadata, collection timestamp, and associated dataset information.'
  })
  async dataSample(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<DataSample> {
    const { domain } = context.state

    return await getRepository(DataSample).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Directive('@privilege(category: "data-sample", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSampleList, {
    description:
      'Retrieves multiple data samples with pagination and filtering support. This query allows searching samples by name and description, and is used for listing and browsing collected data samples across all datasets.'
  })
  async dataSamples(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSampleList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSample),
      params,
      domain,
      searchables: ['name', 'description']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Directive('@privilege(category: "data-sample", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSampleList, {
    description:
      "Retrieves data samples that belong to a specific dataset. This query filters samples by dataset ID and includes searchable fields based on the dataset's key configuration, allowing for targeted data analysis within a particular dataset context."
  })
  async dataSamplesByDataSet(
    @Arg('dataSetId') dataSetId: string,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSampleList> {
    const { domain } = context.state

    const dataSet = await getRepository(DataSet).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id: dataSetId },
      relations: ['dataKeySet']
    })

    if (!dataSet) {
      throw new Error(`dataSet not found by the given dataSetId(${dataSetId})`)
    }

    const dataKeyItems = dataSet.dataKeySet?.dataKeyItems || []
    const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSample),
      params,
      domain,
      alias: 'sample',
      searchables: ['name', 'description'].concat(searchables)
    }).innerJoin('sample.dataSet', 'ds', 'ds.id = :dataSetId', {
      dataSetId
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Directive('@privilege(category: "data-sample", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSampleList, {
    description:
      'Retrieves data samples that belong to datasets using a specific data key set. This query filters samples by key set ID and enables cross-dataset analysis based on shared key configurations, useful for comparing data across related datasets.'
  })
  async dataSamplesByDataKeySet(
    @Arg('dataKeySetId') dataKeySetId: string,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSampleList> {
    const { domain } = context.state

    const dataKeySet = await getRepository(DataKeySet).findOneBy({ id: dataKeySetId })
    const dataKeyItems = dataKeySet?.dataKeyItems || []
    const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSample),
      params,
      domain,
      alias: 'sample',
      searchables: ['name', 'description'].concat(searchables)
    }).innerJoin('sample.dataSet', 'ds', 'ds.dataKeySet = :dataKeySetId', {
      dataKeySetId
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Query(returns => DataSampleList, {
    description:
      'Retrieves data samples within a specified time period for a given dataset. This query supports various period options (today, this month, 30 days, etc.) and is essential for time-based data analysis, trend monitoring, and historical data review.'
  })
  async dataSamplesByPeriod(
    @Arg('period') period: 'today' | 'this month' | '30 days' | 'this year' | '12 months' | 'date range' | 'month',
    @Arg('dataSetName') dataSetName: string,
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSampleList> {
    const { domain } = context.state

    const { date, month } = params.filters.reduce(
      (acc, filter) => {
        if (filter.name === 'date') acc.date = filter.value
        if (filter.name === 'month') acc.month = filter.value
        return acc
      },
      { date: null, month: null }
    )

    const { from, to } = await getISOStringsForPeriod(period, { date, month }, context)

    const dataSet = await getRepository(DataSet).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, name: dataSetName },
      relations: ['dataKeySet']
    })

    if (!dataSet) {
      throw new Error(`dataSet not found by the given dataSetName(${dataSetName})`)
    }

    const { filters = [] } = params || {}
    const alteredFilters = filters.concat([
      {
        name: 'collectedAt',
        operator: 'between',
        value: [from, to]
      }
    ])

    const dataKeyItems = dataSet.dataKeySet?.dataKeyItems || []
    const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSample),
      params: {
        ...params,
        filters: alteredFilters
      },
      domain,
      alias: 'sample',
      searchables: ['name', 'description'].concat(searchables)
    }).innerJoin('sample.dataSet', 'ds', 'ds.id = :dataSetId', {
      dataSetId: dataSet.id
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => [DataItem], {
    description:
      'Retrieves the data items (field definitions) associated with this data sample. These items define the structure and validation rules for the sample data, including field names, types, and constraints.'
  })
  async dataItems(@Root() dataSample: DataSample): Promise<DataItem[]> {
    const dataSetHistory: DataSetHistory =
      dataSample.dataSetId &&
      (await getRepository(DataSetHistory).findOne({
        where: {
          originalId: dataSample.dataSetId,
          version: dataSample.dataSetVersion
        }
      }))

    return dataSetHistory?.dataItems.filter(item => item.active) || []
  }

  @FieldResolver(type => DataSet, {
    description:
      'Retrieves the dataset that this data sample belongs to. This field resolver provides access to the dataset configuration, metadata, and settings associated with the sample.'
  })
  async dataSet(@Root() dataSample: DataSample): Promise<DataSet> {
    return dataSample.dataSetId && (await getRepository(DataSet).findOneBy({ id: dataSample.dataSetId }))
  }

  @FieldResolver(type => DataOoc, {
    description:
      'Retrieves the out-of-control (OOC) record associated with this data sample, if it exists. This field resolver is used when the sample has been flagged as out of control or out of specification, providing access to the corrective action workflow.'
  })
  async dataOoc(@Root() dataSample: DataSample): Promise<DataOoc> {
    if (dataSample.ooc || dataSample.oos) {
      return await getRepository(DataOoc).findOneBy({ dataSample: { id: dataSample.id } })
    }
  }

  @FieldResolver(type => ActivityInstance)
  async reviewActivityInstance(@Root() dataSample: DataSample): Promise<ActivityInstance> {
    return (
      dataSample.reviewActivityInstanceId &&
      (await getRepository(ActivityInstance).findOneBy({ id: dataSample.reviewActivityInstanceId }))
    )
  }

  @FieldResolver(type => Domain)
  async domain(@Root() dataSample: DataSample): Promise<Domain> {
    return dataSample.domainId && (await getRepository(Domain).findOneBy({ id: dataSample.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() dataSample: DataSample): Promise<User> {
    return dataSample.updaterId && (await getRepository(User).findOneBy({ id: dataSample.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() dataSample: DataSample): Promise<User> {
    return dataSample.creatorId && (await getRepository(User).findOneBy({ id: dataSample.creatorId }))
  }
}
